import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import NotesList from "./NotesList"

interface Consultation {
  id: string
  consultation_date: string
  notes: string
  status: string
}

interface Note {
  id: number
  content: string
  date: string
  user_id: number
  user?: {
    username: string
  }
}

interface Patient {
  id: string
  first_name: string
  last_name: string
  birth_date: string
  gender: string
  address?: string
  postal_code?: string
  city?: string
  phone?: string
  email?: string
  civil_status?: string
  attending_physician?: string
  occupation?: string
  number_of_children: number
  children_info?: string
  height?: number
  weight?: number
  target_weight?: number
  weight_history?: string
  lifestyle_alcohol?: boolean
  lifestyle_tobacco?: boolean
  lifestyle_coffee?: boolean
  lifestyle_activity?: string
  family_history?: string[]
  personal_history?: string[]
  other_history?: string
  intoxication?: string
  allergies?: string
  vaccinations?: string
  medical_history?: string
  medications?: string
}

interface ShowProps {
  patient: Patient
  consultations: Consultation[]
  notes: Note[]
}

export default function Show({ patient, consultations, notes }: ShowProps) {
  return (
    <div className="container mx-auto py-8">
      <Head title={`${patient.first_name} ${patient.last_name}`} />
      
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">
            {patient.first_name} {patient.last_name}
          </h1>
          <div className="text-muted-foreground space-y-1">
            <p>Né(e) le {new Date(patient.birth_date).toLocaleDateString()}</p>
            {patient.address && (
              <p>{patient.address}, {patient.postal_code} {patient.city}</p>
            )}
            <div className="flex gap-4 mt-2">
              {patient.phone && <p>Tél: {patient.phone}</p>}
              {patient.email && <p>Email: {patient.email}</p>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" asChild>
            <Link href="/patients">
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href={`/patients/${patient.id}/edit`}>
              Modifier
            </Link>
          </Button>
          <Button asChild>
            <Link href={`/patients/${patient.id}/consultations/new`}>
              Nouvelle Consultation
            </Link>
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Profession</span>
              <span>{patient.occupation || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Médecin traitant</span>
              <span>{patient.attending_physician || "-"}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Enfants</span>
              <div className="text-right">
                <div>{patient.number_of_children || "-"}</div>
                <div className="text-sm text-muted-foreground">{patient.children_info || ""}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Mesures & Mode de vie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="font-medium block text-xs text-muted-foreground">Taille</span>
                <span>{patient.height ? `${patient.height} cm` : "-"}</span>
              </div>
              <div>
                <span className="font-medium block text-xs text-muted-foreground">Poids</span>
                <span>{patient.weight ? `${patient.weight} kg` : "-"}</span>
              </div>
            </div>
            <div className="flex gap-4 pt-2">
              <div className="flex items-center gap-1">
                <span>Alcool</span>
                <span>{patient.lifestyle_alcohol ? "✅" : "❌"}</span>
              </div>
              <div className="flex items-center gap-1">
                <span>Tabac</span>
                <span>{patient.lifestyle_tobacco ? "✅" : "❌"}</span>
              </div>
              <div className="flex items-center gap-1">
                <span>Café</span>
                <span>{patient.lifestyle_coffee ? "✅" : "❌"}</span>
              </div>
            </div>
            <div className="pt-2">
              <span className="font-medium block text-xs text-muted-foreground">Activité</span>
              <span>{patient.lifestyle_activity || "-"}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Antécédents & Historique</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Row 1: Checkboxes Side by Side */}
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold mb-4">Familiaux</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "attaque cérébrale", "troubles cardio-vasculaires", "asthme, eczéma", "goutte", "allergies",
                    "diabète", "obésité", "maladie du sang", "troubles neuropsychiques", "maladies infectieuses",
                    "arthrose", "arthrite", "cancers"
                  ].map((item) => (
                    <div key={item} className="flex items-center space-x-2">
                      <Checkbox
                        id={`family_${item}`}
                        checked={patient.family_history?.includes(item)}
                        disabled
                      />
                      <Label htmlFor={`family_${item}`} className="font-normal text-sm text-muted-foreground">{item}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Personnels (0-10 ans)</h3>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    "rougeole", "scarlatine", "coqueluche", "varicelle", "rubéole",
                    "oreillons", "angine", "rhumes", "grippes", "bronchites",
                    "otites", "asthme", "eczéma"
                  ].map((item) => (
                    <div key={item} className="flex items-center space-x-2">
                      <Checkbox
                        id={`personal_${item}`}
                        checked={patient.personal_history?.includes(item)}
                        disabled
                      />
                      <Label htmlFor={`personal_${item}`} className="font-normal text-sm text-muted-foreground">{item}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2: Autres */}
            <div>
              <h3 className="font-semibold mb-1">Autres</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{patient.other_history || "Aucun"}</p>
            </div>

            {/* Row 3: Medical History & Medications */}
            <div className="grid md:grid-cols-2 gap-8">
               <div>
                <h3 className="font-semibold mb-1">Passé Médical</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{patient.medical_history || "Aucun"}</p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Médicaments / Compléments</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{patient.medications || "Aucun"}</p>
              </div>
            </div>

            {/* Row 4: Allergies etc */}
            <div className="grid grid-cols-3 gap-4 text-sm pt-4 border-t">
              <div>
                <span className="font-medium block text-xs text-muted-foreground">Allergies</span>
                <span>{patient.allergies || "-"}</span>
              </div>
              <div>
                <span className="font-medium block text-xs text-muted-foreground">Vaccinations</span>
                <span>{patient.vaccinations || "-"}</span>
              </div>
              <div>
                <span className="font-medium block text-xs text-muted-foreground">Intoxication</span>
                <span>{patient.intoxication || "-"}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mb-8 items-start">
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Historique des consultations</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Protocole</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consultations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center h-24 text-muted-foreground">
                      Aucune consultation enregistrée.
                    </TableCell>
                  </TableRow>
                ) : (
                  consultations.map((consultation) => (
                    <TableRow key={consultation.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {consultation.consultation_date}
                          {consultation.status === "draft" && (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-600 text-xs py-0 h-5">
                              Brouillon
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate" title={consultation.notes}>
                        {consultation.notes}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" asChild>
                          <Link 
                            href={
                              consultation.status === "draft" 
                                ? `/patients/${patient.id}/consultations/${consultation.id}/edit`
                                : `/patients/${patient.id}/consultations/${consultation.id}`
                            }
                          >
                            {consultation.status === "draft" ? "Reprendre" : "Voir"}
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="h-full">
          <NotesList patientId={patient.id} notes={notes} />
        </div>
      </div>
    </div>
  )
}
